/**
 * Liquiditätsvorschau: Rechnungen der Agentur → Posten der Fachlogik (liquidityForecast). Forderungen sind offene,
 * festgeschriebene Rechnungen der Agentur an Marken und Creator (ohne Stornorechnungen und Ausbuchungen), Auszahlungen
 * freigegebene, noch nicht ausgezahlte Rechnungen und Gutschriften der Creator mit Fälligkeit nach der Auszahlungsregel
 * (app.payout_due_date, 0055): im Modus „nach Zahlung der Marke“ erst ab der Zahlung, vorher ohne Termin.
 */
import { rowAwaitsDelivery, type LiquidityItem } from "@talentkontor/domain";
import type { InvoiceRow } from "../../../data/queries";
import type { PayoutInfo } from "./detail-data";
import { t } from "../../../lib/sprache";

/**
 * Offene Erstattung bzw. Rückforderung einer stornierten Rechnung (K-153): Betrag und Richtung. Rechnung der Agentur → Abfluss
 * (Erstattung an die Marke bzw. den Creator), Rechnung des Creators → Zufluss (Rückforderung der Auszahlung). null, wenn nichts
 * geflossen oder schon erstattet ist.
 */
export function offeneErstattung(i: Pick<InvoiceRow, "status" | "credit_for" | "paid_cents" | "paid_at" | "gross_cents" | "kind"> & { admin_payment_confirmed_at?: string | null }): { kind: "in" | "out"; cents: number } | null {
  if (i.status !== "cancelled" || i.credit_for) return null;
  // K-157: als ausgezahlt vermerkt (Creator hat noch nicht bestätigt) zählt wie bezahlt
  const ausgezahlt = i.kind === "talent_to_agency" && !!i.admin_payment_confirmed_at;
  const cents = Number(i.paid_cents ?? 0) > 0 ? Number(i.paid_cents) : i.paid_at || ausgezahlt ? (i.gross_cents ?? 0) : 0;
  if (cents <= 0) return null;
  return { kind: i.kind === "agency_to_brand" || i.kind === "agency_to_talent" ? "out" : "in", cents };
}
export const erstattungName = (kind: "in" | "out") => (kind === "out" ? t("Erstattung", "Refund") : t("Rückforderung", "Reclaim"));

export function liquidityItems(invoices: readonly InvoiceRow[], payoutInfo: readonly PayoutInfo[], today?: string | null,
  /** Rechnungen in einem offenen Auszahlungslauf: Ausführungsdatum des Laufs (genauer als das Zahlungsziel der Rechnung) */
  laufDatum?: ReadonlyMap<string, string> | null): LiquidityItem[] {
  const info = new Map(payoutInfo.map((p) => [p.invoice_id, p]));
  const out: LiquidityItem[] = [];
  for (const i of invoices) {
    // Praxistest K-153: Storniert, die Zahlung ist aber schon geflossen und noch nicht erstattet bzw. zurückgezahlt
    // („Storniert · Erstattung offen“). Das Geld ist sofort fällig: Rechnung der Agentur → Abfluss an die Marke bzw. den
    // Creator, Rechnung des Creators (schon ausgezahlt) → Rückforderung als Zufluss.
    if (i.status === "cancelled" && !i.credit_for) {
      const e = offeneErstattung(i);
      if (e) out.push({ id: i.id, kind: e.kind, currency: i.currency, amountCents: e.cents, due: today ?? null, waiting: null, label: `${erstattungName(e.kind)} ${i.number ?? ""}`.trim() });
      continue;
    }
    const open = (i.gross_cents ?? 0) - Number(i.paid_cents ?? 0);
    if (open <= 0 || i.credit_for) continue;
    if ((i.kind === "agency_to_brand" || i.kind === "agency_to_talent") && (i.status === "approved" || i.status === "sent") && !i["bad_debt_at"]) {
      // Nicht zugegangen (Rückläufer): keine Frist, ohne Termin wie „noch nicht versendet“ (GELD-R3-7)
      const due = i.status === "sent" && !rowAwaitsDelivery(i) ? i.effective_due_date ?? i.document_due_date : null;
      out.push({ id: i.id, kind: "in", currency: i.currency, amountCents: open, due, waiting: due ? null : "not_sent", label: i.number ?? undefined });
    } else if (i.kind === "talent_to_agency" && (i.status === "approved" || i.status === "sent") && !i.paid_at && !i["admin_payment_confirmed_at"] && !i.in_collection) {
      const p = info.get(i.id);
      const fixed = i.effective_due_date ?? i.document_due_date ?? (i["issuer_due_date"] as string | null | undefined) ?? null;
      // Steht die Rechnung in einem offenen Lauf, fließt das Geld am Ausführungsdatum des Laufs (Nachtest K-153)
      const due = laufDatum?.get(i.id) ?? (p ? (p.payout_due ?? (p.mode === "after_brand_payment" ? null : fixed)) : fixed);
      out.push({ id: i.id, kind: "out", currency: i.currency, amountCents: open, due, waiting: due ? null : "brand_payment", label: i.number ?? undefined });
    }
  }
  return out;
}

/** Offene Creator-Rechnungen, deren Auszahlungstermin die Datenbank liefert (invoice_payout_info). */
export function payoutInvoiceIds(invoices: readonly InvoiceRow[]): string[] {
  return invoices.filter((i) => i.kind === "talent_to_agency" && (i.status === "approved" || i.status === "sent") && !i.paid_at).map((i) => i.id).sort();
}
